import Repository from "@/utils/API/Repository";
import {AxiosResponse} from "axios";
import {CountryAllergenDefinition} from "@/models/Allergen/CountryAllergenDefinition";
import {AllergenDefinition} from "@/models/Allergen/AllergenDefinition";
import {
  AllergenConfigurationData,
  AllergenConfigurationTargetType,
  SaveAllergenConfigurationRequest
} from "@/models/Allergen/AllergenConfigurationData";
import {SavedAllergen, SelectedAllergen} from "@/models/Allergen/SelectedAllergen";
import {
  AllergenSelectionStatusValue,
  getAllergenSelectionStatusValue
} from "@/models/Allergen/AllergenSelectionStatusValue";
import {LabelLanguage} from "@/models/NutritionLabels/LabelLanguage";

export type AllergenPropagationTarget = {
  id: number,
  name: string,
  isCombinedRecipe: boolean,
  configuration?: AllergenConfigurationData,
  parentId?: number | null,
  parentRecipeId?: number | null,
  parentTargetId?: number | null,
  ancestorId?: number | null,
  level?: number | null,
  depth?: number | null,
  lineagePath?: string | null,
  path?: string | null,
}

export type AllergenPropagationChange = {
  allergenId: number,
  allergen: SelectedAllergen | null,
}

export type AllergenPropagationTargetChangeSet = {
  targetId: number,
  changes: AllergenPropagationChange[],
  mayContainText?: Partial<Record<LabelLanguage, string[]>>,
  applyMayContainText?: boolean,
}

export type StoredAllergenConfigurationByTarget = {
  targetId: number,
  configuration: AllergenConfigurationData,
}

export const resource = '/allergen';

function mapSavedAllergen(allergen: SavedAllergen) {
  if (typeof allergen === "number") {
    return {
      id: allergen,
      aliasNames: [],
      status: AllergenSelectionStatusValue.ManuallyAdded,
      aiRecipeAggregateId: null
    };
  }

  if (typeof allergen === "string") {
    return {
      id: 0,
      aliasNames: [],
      status: AllergenSelectionStatusValue.ManuallyAdded,
      aiRecipeAggregateId: null
    };
  }

  return {
    id: allergen.id,
    aliasNames: allergen.aliasNames || [],
    status: getAllergenSelectionStatusValue(allergen.status),
    aiRecipeAggregateId: allergen.aiRecipeAggregateId ?? null
  };
}

export default {
  listAllergens(): Promise<AxiosResponse<AllergenDefinition[]>> {
    return Repository.get(`${resource}/Allergens`, { withCredentials: true });
  },

  listAllergensByCountry(countryId: number): Promise<AxiosResponse<CountryAllergenDefinition[]>> {
    return Repository.get(`${resource}/Countries/${countryId}/Allergens`, { withCredentials: true });
  },

  saveAllergenConfiguration(data: SaveAllergenConfigurationRequest): Promise<AxiosResponse<void>> {
    return Repository.post(`${resource}/SaveAllergenConfiguration`, {
      ...data,
      allergens: (data.allergens || []).map(mapSavedAllergen),
      mayContainText: data.mayContainText || {}
    }, { withCredentials: true });
  },

  getAllergenConfiguration(
    targetType: AllergenConfigurationTargetType,
    targetId: number
  ): Promise<AxiosResponse<AllergenConfigurationData>> {
    return Repository.get(
      `${resource}/GetAllergenConfiguration?targetType=${targetType}&targetId=${targetId}`,
      { withCredentials: true }
    );
  },

  getStoredAllergenConfiguration(
    targetType: AllergenConfigurationTargetType,
    targetId: number
  ): Promise<AxiosResponse<AllergenConfigurationData>> {
    return Repository.get(
      `${resource}/GetStoredAllergenConfiguration?targetType=${targetType}&targetId=${targetId}`,
      { withCredentials: true }
    );
  },

  getStoredAllergenConfigurations(
    targetType: AllergenConfigurationTargetType,
    targetIds: number[]
  ): Promise<AxiosResponse<StoredAllergenConfigurationByTarget[]>> {
    return Repository.post(`${resource}/GetStoredAllergenConfigurations`, {
      targetType,
      targetIds: targetIds || []
    }, { withCredentials: true });
  },

  getAllergenConfigurations(
    targetType: AllergenConfigurationTargetType,
    targetIds: number[]
  ): Promise<AxiosResponse<StoredAllergenConfigurationByTarget[]>> {
    return Repository.post(`${resource}/GetAllergenConfigurations`, {
      targetType,
      targetIds: targetIds || []
    }, { withCredentials: true });
  },

  getPropagationTargets(
    targetType: AllergenConfigurationTargetType,
    targetId: number
  ): Promise<AxiosResponse<AllergenPropagationTarget[]>> {
    return Repository.get(
      `${resource}/GetPropagationTargets?targetType=${targetType}&targetId=${targetId}`,
      { withCredentials: true }
    );
  },

  applyPropagationChanges(
    targetChanges: AllergenPropagationTargetChangeSet[]
  ): Promise<AxiosResponse<void>> {
    return Repository.post(`${resource}/ApplyPropagationChanges`, {
      targetChanges: (targetChanges || []).map((targetChange) => ({
        targetId: targetChange.targetId,
        changes: (targetChange.changes || []).map((change) => ({
          allergenId: change.allergenId,
          allergen: change.allergen ? mapSavedAllergen(change.allergen) : null
        })),
        mayContainText: targetChange.mayContainText || {},
        applyMayContainText: Boolean(targetChange.applyMayContainText)
      }))
    }, { withCredentials: true });
  }
};
